import * as THREE from "three";
import type { VFX } from "../../../../art/vfx.js";
import type { ChestModel } from "./chestModel";

/**
 * The chest stage's atmosphere and the top tiers' finales (chests v8 wow pass): dust drifting in the light all the time,
 * and after the burst — legendary: gold feathers fluttering down; mythic: lightning arcing between the orbiting crystals,
 * the horns' tips burning and embers rising. Stage world units (the chest at ChestModel.Scale). Presentation only.
 */

/** Motes of dust turning in the shaft of light over the chest: points, additive, slow drift; brighter after the burst. */
export class DustMotes {
  readonly points: THREE.Points;
  readonly level = { value: 0.55 };
  private readonly base: Float32Array;
  private t = 0;
  static readonly Count = 140;

  constructor(centre: THREE.Vector3, spread: number) {
    const n = DustMotes.Count, pos = new Float32Array(n * 3), seed = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      pos[i * 3] = centre.x + (Math.random() - 0.5) * spread * 2.4;
      pos[i * 3 + 1] = centre.y + (Math.random() - 0.2) * spread * 2.2;
      pos[i * 3 + 2] = centre.z + (Math.random() - 0.5) * spread * 1.6;
      seed[i] = Math.random();
    }
    this.base = pos.slice();
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("seed", new THREE.BufferAttribute(seed, 1));
    const mat = new THREE.ShaderMaterial({
      uniforms: { uLevel: this.level, uScale: { value: 60 } },
      vertexShader: `attribute float seed; varying float vA; uniform float uScale;
        void main() { vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_Position = projectionMatrix * mv;
          gl_PointSize = uScale * (0.35 + 0.65 * fract(seed * 7.13)) / -mv.z; vA = 0.4 + 0.6 * fract(seed * 3.7); }`,
      fragmentShader: `varying float vA; uniform float uLevel;
        void main() { float d = length(gl_PointCoord - 0.5) * 2.0; float a = pow(max(1e-6, 1.0 - d), 2.0) * vA * uLevel;
          gl_FragColor = vec4(vec3(1.0, 0.86, 0.6) * a, a); }`,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false,
    });
    this.points = new THREE.Points(g, mat);
    this.points.frustumCulled = false;
  }

  update(dt: number): void {
    this.t += dt;
    const a = this.points.geometry.getAttribute("position") as THREE.BufferAttribute, p = a.array as Float32Array;
    for (let i = 0; i < DustMotes.Count; i++) {
      const k = i * 0.37;
      p[i * 3] = this.base[i * 3] + Math.sin(this.t * 0.21 + k) * 0.18;
      p[i * 3 + 1] = this.base[i * 3 + 1] + Math.sin(this.t * 0.13 + k * 1.7) * 0.25 + ((this.t * 0.04 + i * 0.013) % 0.6);
      p[i * 3 + 2] = this.base[i * 3 + 2] + Math.cos(this.t * 0.17 + k) * 0.15;
    }
    a.needsUpdate = true;
  }

  dispose(): void { this.points.geometry.dispose(); (this.points.material as THREE.Material).dispose(); }
}

/** Gold feathers fluttering down from above after a legendary's burst: instanced quads, a feather drawn in the shader. */
export class FeatherFall {
  readonly mesh: THREE.InstancedMesh;
  private readonly state: { p: THREE.Vector3; v: number; ph: number; spin: number; age: number; life: number }[] = [];
  private live = false;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler();
  private readonly s = new THREE.Vector3();
  static readonly Count = 34;

  constructor(private readonly centre: THREE.Vector3, private readonly spread: number) {
    const mat = new THREE.ShaderMaterial({
      vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0); }`,
      fragmentShader: `varying vec2 vUv;
        void main() { vec2 p = vUv * 2.0 - 1.0; float w = 0.42 * (1.0 - p.y * p.y) * (0.8 + 0.2 * sin(p.y * 30.0));
          float body = 1.0 - smoothstep(w - 0.08, w, abs(p.x)); float spine = (1.0 - smoothstep(0.0, 0.05, abs(p.x))) * step(-0.95, p.y);
          float a = max(body * 0.9, spine); if (a < 0.02) discard;
          vec3 col = mix(vec3(1.0, 0.78, 0.3), vec3(1.0, 0.95, 0.75), body * (0.5 + 0.5 * p.y)) * (1.2 + spine * 0.6);
          gl_FragColor = vec4(col, a); }`,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, toneMapped: false,
    });
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.09, 0.32), mat, FeatherFall.Count);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    for (let i = 0; i < FeatherFall.Count; i++) this.state.push({ p: new THREE.Vector3(), v: 0, ph: 0, spin: 0, age: 0, life: 0 });
  }

  start(): void {
    this.live = true;
    this.mesh.visible = true;
    this.state.forEach((f, i) => {
      f.p.set(this.centre.x + (Math.random() - 0.5) * this.spread * 2.6, this.centre.y + 1.6 + Math.random() * 2.2, this.centre.z + (Math.random() - 0.5) * this.spread * 1.4);
      f.v = 0.35 + Math.random() * 0.3; f.ph = Math.random() * 6.28; f.spin = (Math.random() - 0.5) * 3; f.age = -i * 0.05; f.life = 4 + Math.random() * 2;
    });
  }

  update(dt: number): void {
    if (!this.live) return;
    this.state.forEach((f, i) => {
      f.age += dt;
      const on = f.age > 0 && f.age < f.life;
      if (on) { f.p.y -= f.v * dt; f.p.x += Math.sin(f.age * 2.2 + f.ph) * 0.5 * dt; }
      const fade = on ? Math.min(1, f.age * 3, (f.life - f.age) * 2, Math.max(0, f.p.y) * 4) : 0;
      this.e.set(Math.sin(f.age * 2.2 + f.ph) * 0.9, f.age * f.spin, Math.cos(f.age * 1.7 + f.ph) * 0.7);
      this.m.compose(f.p, this.q.setFromEuler(this.e), this.s.setScalar(Math.max(1e-4, fade)));
      this.mesh.setMatrixAt(i, this.m);
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

/**
 * The mythic's finale, run every frame after its burst: lightning arcing between neighbouring crystals now and then
 * (the VFX library's bolts, crimson), embers rising out of the hoard and off the horns' tips. `onArc` lets the stage
 * play the crackle.
 */
export class MythicStorm {
  private t = 0;
  private nextArc = 0.15;
  private nextEmber = 0;
  private readonly a = new THREE.Vector3();
  private readonly b = new THREE.Vector3();
  onArc: (() => void) | null = null;
  /** The horns' tips in the model's own space (art/hq_chest.py `horns`: the tube's end, glTF y-up, front −z). */
  static readonly HornTips = [new THREE.Vector3(-0.326, 0.339, 0.05), new THREE.Vector3(0.326, 0.339, 0.05)];

  update(dt: number, vfx: VFX | null, model: ChestModel, mouth: THREE.Vector3): void {
    if (!vfx) return;
    this.t += dt;
    if (this.t >= this.nextArc) {
      const i = Math.floor(Math.random() * 6), j = (i + 1 + Math.floor(Math.random() * 2)) % 6;
      if (model.partWorld(`shard${i}`, this.a) && model.partWorld(`shard${j}`, this.b)) {
        vfx.lightning(this.a, this.b, { life: 0.18, width: 0.07, color: 0xff5a4a, jag: 0.9 });
        if (Math.random() < 0.35) vfx.lightning(this.a, mouth, { life: 0.14, width: 0.05, color: 0xffb070, jag: 1.1 });
        this.onArc?.();
      }
      this.nextArc = this.t + (this.t < 1.5 ? 0.12 : 0.45) + Math.random() * 0.35;
    }
    if (this.t >= this.nextEmber) {
      vfx.embers(mouth, 2, { color: [1, 0.45, 0.25], radius: 0.4, life: 1.6, size: 0.08 });
      for (const tip of MythicStorm.HornTips) {
        this.a.copy(tip);
        model.root.localToWorld(this.a);
        vfx.embers(this.a, 1, { color: [1, 0.55, 0.3], radius: 0.05, life: 1.0, size: 0.07 });
        if (this.t < 2 || Math.random() < 0.3) vfx.glowPulse(this.a, { size: 0.35, life: 0.35, color: [1, 0.4, 0.25] });
      }
      this.nextEmber = this.t + 0.12;
    }
  }
}

/**
 * The stage's atmosphere and finales together: the dust all along, brighter from the burst; at a legendary's burst the
 * feathers and the coin rain, at a mythic's the storm. `onArc` on each of the storm's arcs.
 */
export class StageFinale {
  private readonly dust: DustMotes;
  private feathers: FeatherFall | null = null;
  private storm: MythicStorm | null = null;
  onArc: (() => void) | null = null;
  static readonly Legendary = 4;
  static readonly Mythic = 5;

  constructor(private readonly scene: THREE.Scene, private readonly centre: THREE.Vector3) {
    this.dust = new DustMotes(centre, 1.4);
    scene.add(this.dust.points);
  }

  burst(model: ChestModel): void {
    this.dust.level.value = 1;
    if (model.rank === StageFinale.Legendary) {
      this.feathers = new FeatherFall(this.centre, 1.1);
      this.scene.add(this.feathers.mesh);
      this.feathers.start();
      model.show.rain(this.centre, 1.2, 3.2);
    }
    if (model.rank === StageFinale.Mythic) { this.storm = new MythicStorm(); this.storm.onArc = () => this.onArc?.(); }
  }

  update(dt: number, vfx: VFX | null, model: ChestModel): void {
    this.dust.update(dt);
    this.feathers?.update(dt);
    this.storm?.update(dt, vfx, model, model.show.mouthAt);
  }

  dispose(): void { this.dust.dispose(); this.feathers?.dispose(); }
}
